import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { holidaysAPI } from '../services/api';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar, Plus, Trash, FloppyDisk, Buildings } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HolidaysPage = () => {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';

  const [offices, setOffices] = useState([]);
  const [selectedOfficeId, setSelectedOfficeId] = useState('');
  const [config, setConfig] = useState({
    holiday_billing_multiplier: 1.5,
    holiday_pay_multiplier: 1.5,
    holidays: [],
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newDate, setNewDate] = useState('');
  const [newName, setNewName] = useState('');

  // Bootstrap office selection — super admins pick from list, others use their own office_id
  useEffect(() => {
    (async () => {
      if (isSuperAdmin) {
        try {
          const res = await axios.get(`${API_BASE}/offices`, { withCredentials: true });
          setOffices(res.data || []);
          if (res.data?.[0]?.id) setSelectedOfficeId(res.data[0].id);
        } catch (e) {
          toast.error('Failed to load offices');
        }
      } else if (user?.office_id) {
        setSelectedOfficeId(user.office_id);
      } else {
        setLoading(false);
        toast.error('You are not linked to any office');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  useEffect(() => {
    if (selectedOfficeId) loadHolidays(selectedOfficeId);
  }, [selectedOfficeId]);

  const loadHolidays = async (officeId) => {
    setLoading(true);
    try {
      const res = await holidaysAPI.get(officeId);
      setConfig({
        holiday_billing_multiplier: res.data.holiday_billing_multiplier ?? 1.5,
        holiday_pay_multiplier: res.data.holiday_pay_multiplier ?? 1.5,
        holidays: res.data.holidays || [],
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load holidays');
    } finally { setLoading(false); }
  };

  const saveMultipliers = async () => {
    setSaving(true);
    try {
      await holidaysAPI.update(selectedOfficeId, {
        holiday_billing_multiplier: parseFloat(config.holiday_billing_multiplier),
        holiday_pay_multiplier: parseFloat(config.holiday_pay_multiplier),
      });
      toast.success('Multipliers saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const addHoliday = async () => {
    if (!newDate) { toast.error('Pick a date'); return; }
    const name = (newName || '').trim() || format(new Date(newDate + 'T00:00:00'), 'MMM d');
    try {
      await holidaysAPI.add(selectedOfficeId, { date: newDate, name });
      toast.success(`Added ${name}`);
      setNewDate(''); setNewName('');
      loadHolidays(selectedOfficeId);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add');
    }
  };

  const removeHoliday = async (date) => {
    try {
      await holidaysAPI.remove(selectedOfficeId, date);
      toast.success('Holiday removed');
      loadHolidays(selectedOfficeId);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to remove');
    }
  };

  // Convenience: load US federal holidays for a given year as a one-click action
  const preloadFederal = async () => {
    const y = new Date().getFullYear();
    const next = y + 1;
    const list = [
      ...federalHolidaysForYear(y),
      ...federalHolidaysForYear(next),
    ];
    const existing = new Set((config.holidays || []).map(h => h.date));
    const fresh = list.filter(h => !existing.has(h.date));
    if (fresh.length === 0) { toast.info('All federal holidays are already in your list'); return; }
    try {
      await holidaysAPI.update(selectedOfficeId, {
        holidays: [...(config.holidays || []), ...fresh],
      });
      toast.success(`Added ${fresh.length} US federal holidays`);
      loadHolidays(selectedOfficeId);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to preload');
    }
  };

  if (!isSuperAdmin && user?.role !== 'supervisor' && user?.role !== 'admin') {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <h3 className="font-heading text-lg font-medium">Access Restricted</h3>
        <p className="text-muted-foreground text-sm mt-1">Only admins can manage holiday billing</p>
      </div>
    );
  }

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>;

  return (
    <div className="space-y-6 fade-in" data-testid="holidays-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-semibold">Holiday Billing</h1>
          <p className="text-muted-foreground mt-1 max-w-2xl">
            Visits scheduled on a holiday are billed at a multiple of the client's standard rate (and caregivers earn a holiday pay bump). Holiday rate beats weekend and overtime when a date matches.
          </p>
        </div>
        {isSuperAdmin && (
          <div className="flex flex-col gap-1 min-w-[260px]" data-testid="office-picker">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Office</Label>
            <Select value={selectedOfficeId} onValueChange={setSelectedOfficeId}>
              <SelectTrigger className="rounded-xl">
                <SelectValue placeholder="Select office" />
              </SelectTrigger>
              <SelectContent>
                {offices.map(o => (
                  <SelectItem key={o.id} value={o.id}>
                    <span className="flex items-center gap-2"><Buildings weight="duotone" className="w-4 h-4" />{o.name}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {/* Multipliers */}
      <Card className="rounded-2xl border border-border">
        <CardHeader>
          <CardTitle className="font-heading text-lg">Rate multipliers</CardTitle>
          <CardDescription>Applied to the client's standard billing rate and the caregiver's standard pay rate on holidays.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label>Holiday billing multiplier (× client standard rate)</Label>
              <Input
                type="number" step="0.05" min="0.5"
                value={config.holiday_billing_multiplier}
                onChange={(e) => setConfig({ ...config, holiday_billing_multiplier: e.target.value })}
                className="rounded-xl"
                data-testid="holiday-bill-mult"
              />
              <p className="text-xs text-muted-foreground">e.g. 1.5 means time-and-a-half client billing.</p>
            </div>
            <div className="space-y-1">
              <Label>Holiday pay multiplier (× caregiver standard pay rate)</Label>
              <Input
                type="number" step="0.05" min="0.5"
                value={config.holiday_pay_multiplier}
                onChange={(e) => setConfig({ ...config, holiday_pay_multiplier: e.target.value })}
                className="rounded-xl"
                data-testid="holiday-pay-mult"
              />
              <p className="text-xs text-muted-foreground">e.g. 2.0 means caregivers earn double on holidays.</p>
            </div>
          </div>
          <div className="flex justify-end">
            <Button className="rounded-xl gap-2" onClick={saveMultipliers} disabled={saving} data-testid="save-multipliers-btn">
              <FloppyDisk weight="bold" className="w-4 h-4" />
              {saving ? 'Saving...' : 'Save multipliers'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Holiday list */}
      <Card className="rounded-2xl border border-border">
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <CardTitle className="font-heading text-lg">Holidays</CardTitle>
              <CardDescription>{config.holidays.length} configured — these dates trigger holiday rates for billing & payroll.</CardDescription>
            </div>
            <Button variant="outline" className="rounded-xl gap-2" onClick={preloadFederal} data-testid="preload-federal-btn">
              <Calendar weight="bold" className="w-4 h-4" />
              Preload US federal holidays
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Add row */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end p-4 rounded-xl bg-muted/30">
            <div className="md:col-span-3 space-y-1">
              <Label className="text-xs">Date</Label>
              <Input type="date" value={newDate} onChange={(e) => setNewDate(e.target.value)} className="rounded-xl" data-testid="new-holiday-date" />
            </div>
            <div className="md:col-span-7 space-y-1">
              <Label className="text-xs">Name</Label>
              <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="e.g. Memorial Day" className="rounded-xl" data-testid="new-holiday-name" />
            </div>
            <div className="md:col-span-2">
              <Button className="w-full rounded-xl gap-2" onClick={addHoliday} data-testid="add-holiday-btn">
                <Plus weight="bold" className="w-4 h-4" /> Add
              </Button>
            </div>
          </div>

          {/* List */}
          {config.holidays.length === 0 ? (
            <div className="text-center py-10 text-muted-foreground">
              <Calendar weight="duotone" className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No holidays configured yet.</p>
              <p className="text-xs mt-1">Add dates above or preload US federal holidays.</p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {config.holidays.map(h => {
                const d = new Date(h.date + 'T00:00:00');
                const dow = format(d, 'EEE');
                return (
                  <div key={h.date} className="flex items-center justify-between py-3" data-testid={`holiday-row-${h.date}`}>
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl bg-primary/10 flex flex-col items-center justify-center">
                        <span className="text-[10px] uppercase text-primary font-semibold">{format(d, 'MMM')}</span>
                        <span className="text-sm font-heading font-bold">{format(d, 'd')}</span>
                      </div>
                      <div>
                        <p className="font-medium">{h.name}</p>
                        <p className="text-xs text-muted-foreground">{format(d, 'EEEE, MMMM d, yyyy')} <Badge variant="secondary" className="ml-2 text-[10px] rounded-full">{dow}</Badge></p>
                      </div>
                    </div>
                    <Button variant="ghost" size="sm" className="rounded-xl text-destructive hover:bg-destructive/10" onClick={() => removeHoliday(h.date)} data-testid={`remove-holiday-${h.date}`}>
                      <Trash weight="bold" className="w-4 h-4" />
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// US federal holidays — observed dates roughly computed (fixed-date + Monday-after when on weekend
// for July 4 and Christmas; floating ones use a quick rule). The user can edit/remove afterwards.
function federalHolidaysForYear(y) {
  // helpers
  const nthWeekday = (year, monthZeroIdx, weekday, n) => {
    // weekday: 0=Sun..6=Sat
    const first = new Date(year, monthZeroIdx, 1);
    const offset = (weekday - first.getDay() + 7) % 7;
    return new Date(year, monthZeroIdx, 1 + offset + (n - 1) * 7);
  };
  const lastWeekday = (year, monthZeroIdx, weekday) => {
    const last = new Date(year, monthZeroIdx + 1, 0);
    const offset = (last.getDay() - weekday + 7) % 7;
    return new Date(year, monthZeroIdx, last.getDate() - offset);
  };
  const observed = (d) => {
    // Saturday → Friday, Sunday → Monday (federal rule for fixed-date holidays)
    if (d.getDay() === 6) return new Date(d.getFullYear(), d.getMonth(), d.getDate() - 1);
    if (d.getDay() === 0) return new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1);
    return d;
  };
  const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return [
    { date: fmt(observed(new Date(y, 0, 1))), name: "New Year's Day" },
    { date: fmt(nthWeekday(y, 0, 1, 3)), name: 'Martin Luther King Jr. Day' },        // 3rd Mon of Jan
    { date: fmt(nthWeekday(y, 1, 1, 3)), name: "Presidents' Day" },                   // 3rd Mon of Feb
    { date: fmt(lastWeekday(y, 4, 1)), name: 'Memorial Day' },                        // last Mon of May
    { date: fmt(observed(new Date(y, 5, 19))), name: 'Juneteenth' },                  // Jun 19
    { date: fmt(observed(new Date(y, 6, 4))), name: 'Independence Day' },             // Jul 4
    { date: fmt(nthWeekday(y, 8, 1, 1)), name: 'Labor Day' },                         // 1st Mon of Sep
    { date: fmt(nthWeekday(y, 10, 4, 4)), name: 'Thanksgiving Day' },                 // 4th Thu of Nov
    { date: fmt(observed(new Date(y, 11, 25))), name: 'Christmas Day' },              // Dec 25
  ];
}

export default HolidaysPage;
